// Hyperliquid's public websocket, one for the page: papertrade executes at Hyperliquid's BBO mid,
// so its touch is the price. Subscriptions are counted and sent again on every open; a quiet
// socket is pinged at 30 s and dropped at 60 s, then dialled again after 250 ms doubling to 20 s,
// with jitter.

import { createStore } from "../state/store";

export const WS_URL = "wss://api.hyperliquid.xyz/ws";

export type Level = { px: string; sz: string; n: number };
export type Bbo = { coin: string; time: number; bid: Level | null; ask: Level | null };
export type AssetCtx = {
  coin: string;
  prevDayPx: string;
  markPx: string;
  midPx: string | null;
  oraclePx: string;
  dayNtlVlm: string;
  openInterest: string;
  funding: string;
};
export type Candle = { t: number; T: number; s: string; i: string; o: string; c: string; h: string; l: string; v: string; n: number };

export type Subscription =
  | { type: "bbo"; coin: string }
  | { type: "activeAssetCtx"; coin: string }
  | { type: "candle"; coin: string; interval: string };

export type FeedStatus = "connecting" | "open" | "closed";

export const feedStatus = createStore<FeedStatus>("connecting");
/** The latest touch per coin. */
export const bbos = createStore<Record<string, Bbo>>({});
/** The latest day context per coin. */
export const contexts = createStore<Record<string, AssetCtx>>({});

type CandleListener = (candle: Candle) => void;
const candleListeners = new Map<string, Set<CandleListener>>();

const counts = new Map<string, number>();
const subscriptions = new Map<string, Subscription>();

let socket: WebSocket | null = null;
let attempt = 0;
let heard = Date.now();
let watch: ReturnType<typeof setInterval> | null = null;
let redial: ReturnType<typeof setTimeout> | null = null;

const keyOf = (sub: Subscription) => JSON.stringify(sub);

function send(message: unknown) {
  if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(message));
}

function onMessage(event: MessageEvent<string>) {
  heard = Date.now();
  let message: { channel?: string; data?: unknown };
  try {
    message = JSON.parse(event.data) as typeof message;
  } catch {
    return;
  }
  switch (message.channel) {
    case "bbo": {
      const data = message.data as { coin: string; time: number; bbo: [Level | null, Level | null] };
      bbos.set((all) => ({ ...all, [data.coin]: { coin: data.coin, time: data.time, bid: data.bbo[0], ask: data.bbo[1] } }));
      break;
    }
    case "activeAssetCtx": {
      const data = message.data as { coin: string; ctx: Omit<AssetCtx, "coin"> };
      contexts.set((all) => ({ ...all, [data.coin]: { coin: data.coin, ...data.ctx } }));
      break;
    }
    case "candle": {
      const candle = message.data as Candle;
      candleListeners.get(`${candle.s}:${candle.i}`)?.forEach((listener) => listener(candle));
      break;
    }
    default:
      break;
  }
}

function dial() {
  redial = null;
  feedStatus.set(attempt === 0 ? "connecting" : "closed");
  const ws = new WebSocket(WS_URL);
  socket = ws;
  heard = Date.now();
  ws.onopen = () => {
    attempt = 0;
    heard = Date.now();
    feedStatus.set("open");
    subscriptions.forEach((subscription) => send({ method: "subscribe", subscription }));
  };
  ws.onmessage = onMessage;
  ws.onclose = () => {
    if (socket !== ws) return;
    socket = null;
    feedStatus.set("closed");
    later();
  };
  ws.onerror = () => ws.close();
}

function later() {
  if (redial || subscriptions.size === 0) return;
  const base = Math.min(20_000, 250 * 2 ** Math.min(attempt, 7));
  attempt += 1;
  redial = setTimeout(dial, base / 2 + Math.random() * (base / 2));
}

function ensure() {
  if (!watch) {
    watch = setInterval(() => {
      const quiet = Date.now() - heard;
      if (quiet > 60_000) socket?.close();
      else if (quiet > 30_000) send({ method: "ping" });
    }, 10_000);
  }
  if (!socket && !redial) dial();
}

/** Holds `subscription` until the returned function is called. */
export function hold(subscription: Subscription): () => void {
  const key = keyOf(subscription);
  const count = counts.get(key) ?? 0;
  counts.set(key, count + 1);
  if (count === 0) {
    subscriptions.set(key, subscription);
    send({ method: "subscribe", subscription });
  }
  ensure();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const left = (counts.get(key) ?? 1) - 1;
    if (left > 0) {
      counts.set(key, left);
      return;
    }
    counts.delete(key);
    subscriptions.delete(key);
    send({ method: "unsubscribe", subscription });
  };
}

/** Each live candle of `coin` at `interval`, while held. */
export function onCandle(coin: string, interval: string, listener: CandleListener): () => void {
  const key = `${coin}:${interval}`;
  const set = candleListeners.get(key) ?? new Set();
  set.add(listener);
  candleListeners.set(key, set);
  const release = hold({ type: "candle", coin, interval });
  return () => {
    set.delete(listener);
    release();
  };
}

// a tab coming back after sleep dials at once rather than waiting out its delay
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && !socket && subscriptions.size > 0) {
      if (redial) clearTimeout(redial);
      redial = null;
      dial();
    }
  });
}
